import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BulkGroundingActionPayload,
  BulkGroundingService,
  InventoryManagementSearchResult,
  MultiResultModalItemResult
} from 'remarketing-angular-library';

import { MultiResultHappyPathData, MultiResultModalHelperService } from '../multi-result-modal-helper/multi-result-modal-helper.service';
import {
  MultiResultContentBase
} from '../../components/action-list-modals/multi-result-modal/multi-result-modal.component';
import { UpdateResultSummary } from '../../interfaces/update-vehicles-action.interface';
import { TranslocoService } from '@jsverse/transloco';
import { SaComponent, SaService } from 'smartauction-frontend-micro-library';
import { takeUntil } from 'rxjs/operators';
import { GroundingAction } from '../../enums/inventory-management.enum';

@Injectable({
  providedIn: 'root'
})
export class BulkGroundingHelperService extends SaService {

  protected groundingText = {
    groundSuccessLabelSingular: '',
    groundSuccessLabelPlural: '',
    ungroundSuccessLabelSingular: '',
    ungroundSuccessLabelPlural: '',
    failureLabelSingular: '',
    failureLabelPlural: '',
    updateHeaderLabelSingular: '',
    updateHeaderLabelPlural: '',
    errorMessage: '',
    errorHeaderText: '',
    textMultipleTenantSelectedErrorMessage: '',
    noApplicableVehiclesSelectedMessage: ''
  };

  private readonly translocoService: TranslocoService;
  private readonly bulkGroundingService: BulkGroundingService;
  private readonly multiResultModalHelperService: MultiResultModalHelperService;

  constructor() {
    super();
    this.translocoService = inject(TranslocoService);
    this.bulkGroundingService = inject(BulkGroundingService);
    this.multiResultModalHelperService = inject(MultiResultModalHelperService);
  }

  filterSelectedVehiclesBasedOnChosenGroundingAction(groundingAction: GroundingAction, selectedVehicles: InventoryManagementSearchResult[]): InventoryManagementSearchResult[] {
    if(groundingAction === GroundingAction.GROUND) {
      return selectedVehicles.filter(vehicle => this.isInUngroundedState(vehicle));
    } else if (groundingAction === GroundingAction.UNGROUND) {
      return  selectedVehicles.filter(vehicle => this.isInGroundedState(vehicle));
    } else {
      console.log('Invalid Grounding Action attempted. Grounding action: ', groundingAction);
      return [];
    }
  }

  isInGroundedState(auctionItem: InventoryManagementSearchResult): boolean {
    return !!auctionItem?.groundedDate?.trim();
  }

  isInUngroundedState(auctionItem: InventoryManagementSearchResult): boolean {
    return !auctionItem?.groundedDate?.length;
  }

  submitGroundingAction(groundingAction: GroundingAction, vehiclesApplicableToAction: InventoryManagementSearchResult[]): Observable<MultiResultModalItemResult[]> {
    this.getBulkGroundingTranslations();
    const bulkGroundingPayload = this.buildBulkGroundingPayload(groundingAction, vehiclesApplicableToAction);
    return this.bulkGroundingService.submitBulkGroundingAction(bulkGroundingPayload);
  }

  buildBulkGroundingPayload(groundingAction: string, vehiclesApplicableToAction: InventoryManagementSearchResult[]): BulkGroundingActionPayload {
    const selectedAuctionItemUuids = vehiclesApplicableToAction.map(item => item.vehicleUuid);
    return {
      auctionItemUuids: selectedAuctionItemUuids,
      groundedFlag: groundingAction === GroundingAction.GROUND,
      groundedDate: new Date(Date.now()).toISOString()
    } as BulkGroundingActionPayload;
  }

  buildBulkGroundingHappyPathContent(formattedResponse: UpdateResultSummary, groundingAction: GroundingAction, selectedVehicleCount: number): MultiResultContentBase {
    const actionSuccessLabelSingular = groundingAction === GroundingAction.GROUND ? this.groundingText.groundSuccessLabelSingular : this.groundingText.ungroundSuccessLabelSingular;
    const actionSuccessLabelPlural = groundingAction === GroundingAction.GROUND ? this.groundingText.groundSuccessLabelPlural : this.groundingText.ungroundSuccessLabelPlural;

    const happyPathData = {
      formattedResponse: formattedResponse,
      selectedVehicleCount: selectedVehicleCount,
      headerLabelSingular: this.groundingText.updateHeaderLabelSingular,
      headerLabelPlural: this.groundingText.updateHeaderLabelPlural,
      successLabelSingular: actionSuccessLabelSingular,
      successLabelPlural: actionSuccessLabelPlural,
      failureLabelSingular: this.groundingText.failureLabelSingular,
      failureLabelPlural: this.groundingText.failureLabelPlural
    } as MultiResultHappyPathData;

    return this.multiResultModalHelperService.buildHappyPathTemplateContent(happyPathData);
  }

  buildBulkGroundingErrorContent(selectedVehicleCount: number): MultiResultContentBase {
    this.getBulkGroundingTranslations();
    return this.multiResultModalHelperService.buildErrorContent(this.groundingText.errorHeaderText, this.groundingText.errorMessage, selectedVehicleCount);
  }

  buildMultipleTenantsSelectedErrorContent(selectedVehicleCount: number): MultiResultContentBase {
    this.getBulkGroundingTranslations();
    return this.multiResultModalHelperService.buildErrorContent(this.groundingText.errorHeaderText, this.groundingText.textMultipleTenantSelectedErrorMessage, selectedVehicleCount);
  }

  buildNoApplicableVehiclesErrorContent(selectedVehicleCount: number): MultiResultContentBase {
    this.getBulkGroundingTranslations();
    return this.multiResultModalHelperService.buildErrorContent(this.groundingText.errorHeaderText, this.groundingText.noApplicableVehiclesSelectedMessage, selectedVehicleCount);
  }

  getBulkGroundingTranslations(): void {
    this.translocoService.selectTranslateObject('sellModule.manageInventory.actionListModal.groundingActionModal').pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.groundingText.groundSuccessLabelSingular = translate.textSuccessLabelGroundedSingular;
      this.groundingText.groundSuccessLabelPlural = translate.textSuccessLabelGroundedPlural;
      this.groundingText.ungroundSuccessLabelSingular = translate.textSuccessLabelUngroundedSingular;
      this.groundingText.ungroundSuccessLabelPlural = translate.textSuccessLabelUngroundedPlural;
      this.groundingText.failureLabelSingular = translate.textFailureLabelSingular;
      this.groundingText.failureLabelPlural = translate.textFailureLabelPlural;
      this.groundingText.updateHeaderLabelSingular = translate.textVehicleUpdated;
      this.groundingText.updateHeaderLabelPlural = translate.textVehiclesUpdated;
      this.groundingText.errorMessage = translate.textErrorMessage;
      this.groundingText.errorHeaderText = translate.textErrorHeader;
      this.groundingText.textMultipleTenantSelectedErrorMessage = translate.textMultipleTenantSelectedErrorMessage;
      this.groundingText.noApplicableVehiclesSelectedMessage = translate.noApplicableVehiclesSelectedMessage;
    });
  }
}
